import type { AssetsByLanguageType, SupportedLanguage } from '@web-rendering/groundcontrol-shared-types';

export const COUNTRY_LANGUAGE_MAPPING: { [key: string]: SupportedLanguage } = {
  AE: 'ar', // United Arab Emirates (Arabic)
  BD: 'bn',
  BG: 'bg',
  BR: 'pt-br',
  CA: 'en',
  CN: 'zh-cn',
  CZ: 'cs',
  DE: 'de',
  EG: 'ar', // Egypt (Arabic)
  ES: 'es',
  FR: 'fr',
  GB: 'en',
  HK: 'zh-hk',
  HR: 'hr',
  HU: 'hu',
  ID: 'id',
  IN: 'hi',
  IT: 'it',
  JP: 'ja',
  KR: 'ko',
  LK: 'ta',
  MX: 'es-mx',
  PH: 'tl',
  PL: 'pl',
  RO: 'ro',
  RS: 'sr',
  SA: 'ar', // Saudi Arabia (Arabic)
  SK: 'sk',
  TH: 'th',
  US: 'en',
  VN: 'vi'
};

export function isSupportedLanguage(language: string | undefined): language is SupportedLanguage {
  if (!language) return false;
  return Object.values(COUNTRY_LANGUAGE_MAPPING).includes(language as unknown as SupportedLanguage);
}

export const ASSETS_BY_LANGUAGE: AssetsByLanguageType = {
  en: {
    title: 'Click Here for More Information',
    description: "Don't miss this content from our sponsor",
    promotedByText: 'Ad by',
    advertiser: 'Sponsor',
    ctaText: 'See More',
    days: 'Days',
    hours: 'Hours',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  fr: {
    title: "Cliquez ici pour plus d'information",
    description: 'Ne manquez pas ce contenu de notre annonceur',
    promotedByText: 'Publicité par',
    advertiser: 'Annonceur',
    ctaText: 'En savoir plus',
    days: 'Jours',
    hours: 'Heures',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  de: {
    title: 'Klicken Sie hier für weitere Informationen',
    description: 'Verpassen Sie nicht diesen Inhalt von unserem Sponsor',
    promotedByText: 'Anzeige von',
    advertiser: 'Sponsor',
    ctaText: 'Mehr sehen',
    days: 'Tages',
    hours: 'Std',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  es: {
    title: 'Haga clic aquí para más información',
    description: 'No te pierdas este contenido de nuestro patrocinador',
    promotedByText: 'Anuncio por',
    advertiser: 'Patrocinador',
    ctaText: 'Ver más',
    days: 'Dias',
    hours: 'Horas',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Seg'
  },
  'es-mx': {
    title: 'Haga clic aquí para más información',
    description: 'No te pierdas este contenido de nuestro patrocinador',
    promotedByText: 'Anuncio por',
    advertiser: 'Patrocinador',
    ctaText: 'Ver más',
    days: 'Dias',
    hours: 'Horas',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Seg'
  },
  it: {
    title: 'Clicca qui per maggiori informazioni',
    description: 'Non perdere questo contenuto del nostro sponsor',
    promotedByText: 'Annuncio per',
    advertiser: 'Sponsor',
    ctaText: 'Vedi altro',
    days: 'Giorni',
    hours: 'Ore',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  'pt-br': {
    // AI generated
    title: 'Clique aqui para obter mais informações',
    description: 'Não perca este conteúdo do nosso patrocinador',
    promotedByText: 'Anúncio do',
    advertiser: 'Patrocinador',
    ctaText: 'Ver mais',
    days: 'Dias',
    hours: 'Horas',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Seg'
  },
  ar: {
    // AI generated
    title: 'انقر هنا لمزيد من المعلومات',
    description: 'لا تفوت هذا المحتوى من راعينا',
    promotedByText: 'إعلان بواسطة',
    advertiser: 'الراعي',
    ctaText: 'اعرف المزيد',
    days: 'أيام',
    hours: 'ساعات',
    minutesAbbreviation: 'دق',
    secondsAbbreviation: 'ث'
  },
  bg: {
    // AI generated
    title: 'Кликнете тук за повече информация',
    description: 'Не пропускайте това съдържание от нашия спонсор',
    promotedByText: 'Реклама от',
    advertiser: 'Спонсор',
    ctaText: 'Виж повече',
    days: 'Дни',
    hours: 'Часа',
    minutesAbbreviation: 'Мин',
    secondsAbbreviation: 'Сек'
  },
  bn: {
    // AI generated
    title: 'আরও তথ্যের জন্য এখানে ক্লিক করুন',
    description: 'আমাদের স্পনসরের এই বিষয়বস্তুটি মিস করবেন না',
    promotedByText: 'প্রচারক',
    advertiser: 'স্পনসর',
    ctaText: 'আরও দেখুন',
    days: 'দিন',
    hours: 'ঘণ্টা',
    minutesAbbreviation: 'মিনিট',
    secondsAbbreviation: 'সেকেন্ড'
  },
  cs: {
    // AI generated
    title: 'Klikněte zde pro více informací',
    description: 'Nezmeškejte tento obsah od našeho sponzora',
    promotedByText: 'Reklama od',
    advertiser: 'Sponzor',
    ctaText: 'Zjistit více',
    days: 'Dny',
    hours: 'Hodiny',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  hi: {
    // AI generated
    title: 'अधिक जानकारी के लिए यहां क्लिक करें',
    description: 'हमारे प्रायोजक की इस सामग्री को न चूकें',
    promotedByText: 'विज्ञापन द्वारा',
    advertiser: 'प्रायोजक',
    ctaText: 'और देखें',
    days: 'दिन',
    hours: 'घंटे',
    minutesAbbreviation: 'मिनट',
    secondsAbbreviation: 'सेकंड'
  },
  hr: {
    // AI generated
    title: 'Kliknite ovdje za više informacija',
    description: 'Ne propustite ovaj sadržaj našeg sponzora',
    promotedByText: 'Oglas od',
    advertiser: 'Sponzor',
    ctaText: 'Pogledaj više',
    days: 'Dana',
    hours: 'Sati',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  hu: {
    // AI generated
    title: 'Kattintson ide további információkért',
    description: 'Ne hagyja ki ezt a tartalmat a szponzorunktól',
    promotedByText: 'Hirdetés:',
    advertiser: 'Szponzor',
    ctaText: 'Tovább',
    days: 'Nap',
    hours: 'Óra',
    minutesAbbreviation: 'Perc',
    secondsAbbreviation: 'Mp'
  },
  id: {
    // AI generated
    title: 'Klik di sini untuk informasi lebih lanjut',
    description: 'Jangan lewatkan konten ini dari sponsor kami',
    promotedByText: 'Iklan oleh',
    advertiser: 'Sponsor',
    ctaText: 'Lihat Selengkapnya',
    days: 'Hari',
    hours: 'Jam',
    minutesAbbreviation: 'Menit',
    secondsAbbreviation: 'Detik'
  },
  ja: {
    // AI generated
    title: '詳細はこちらをクリック',
    description: 'スポンサーのコンテンツをお見逃しなく',
    promotedByText: '広告：',
    advertiser: 'スポンサー',
    ctaText: 'もっと見る',
    days: '日',
    hours: '時間',
    minutesAbbreviation: '分',
    secondsAbbreviation: '秒'
  },
  ko: {
    // AI generated
    title: '자세한 내용을 보려면 여기를 클릭하세요',
    description: '스폰서의 콘텐츠를 놓치지 마세요',
    promotedByText: '광고:',
    advertiser: '스폰서',
    ctaText: '자세히 보기',
    days: '일',
    hours: '시간',
    minutesAbbreviation: '분',
    secondsAbbreviation: '초'
  },
  pl: {
    // AI generated
    title: 'Kliknij tutaj, aby uzyskać więcej informacji',
    description: 'Nie przegap tej treści od naszego sponsora',
    promotedByText: 'Reklama od',
    advertiser: 'Sponsor',
    ctaText: 'Zobacz więcej',
    days: 'Dni',
    hours: 'Godziny',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  ro: {
    // AI generated
    title: 'Faceți clic aici pentru mai multe informații',
    description: 'Nu ratați acest conținut de la sponsorul nostru',
    promotedByText: 'Reclamă de la',
    advertiser: 'Sponsor',
    ctaText: 'Află mai multe',
    days: 'Zile',
    hours: 'Ore',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  sk: {
    // AI generated
    title: 'Kliknite sem pre viac informácií',
    description: 'Nezmeškajte tento obsah od nášho sponzora',
    promotedByText: 'Reklama od',
    advertiser: 'Sponzor',
    ctaText: 'Zobraziť viac',
    days: 'Dni',
    hours: 'Hodiny',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  sr: {
    // AI generated
    title: 'Kliknite ovde za više informacija',
    description: 'Ne propustite ovaj sadržaj od našeg sponzora',
    promotedByText: 'Oglas od',
    advertiser: 'Sponzor',
    ctaText: 'Pogledaj više',
    days: 'Dana',
    hours: 'Sati',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sek'
  },
  ta: {
    // AI generated
    title: 'மேலும் தகவலுக்கு இங்கே கிளிக் செய்யவும்',
    description: 'எங்கள் விளம்பரதாரரின் உள்ளடக்கத்தை தவறவிடாதீர்கள்',
    promotedByText: 'விளம்பரம்:',
    advertiser: 'ஸ்பான்சர்',
    ctaText: 'மேலும் பார்க்க',
    days: 'நாட்கள்',
    hours: 'மணி',
    minutesAbbreviation: 'நிமி',
    secondsAbbreviation: 'வினா'
  },
  th: {
    // AI generated
    title: 'คลิกที่นี่เพื่อดูข้อมูลเพิ่มเติม',
    description: 'อย่าพลาดเนื้อหานี้จากผู้สนับสนุนของเรา',
    promotedByText: 'โฆษณาโดย',
    advertiser: 'ผู้สนับสนุน',
    ctaText: 'ดูเพิ่มเติม',
    days: 'วัน',
    hours: 'ชั่วโมง',
    minutesAbbreviation: 'นาที',
    secondsAbbreviation: 'วินาที'
  },
  'tl-en': {
    // AI generated
    title: 'Click dito para sa karagdagang impormasyon',
    description: 'Huwag palampasin ang nilalamang ito mula sa aming sponsor',
    promotedByText: 'Ad mula sa',
    advertiser: 'Sponsor',
    ctaText: 'Tingnan pa',
    days: 'Araw',
    hours: 'Oras',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  tl: {
    // AI generated
    title: 'Mag-click dito para sa karagdagang impormasyon',
    description: 'Huwag palampasin ang nilalamang ito mula sa aming sponsor',
    promotedByText: 'Anunsyo mula sa',
    advertiser: 'Tagasuporta',
    ctaText: 'Tingnan pa',
    days: 'Araw',
    hours: 'Oras',
    minutesAbbreviation: 'Min',
    secondsAbbreviation: 'Sec'
  },
  vi: {
    // AI generated
    title: 'Nhấn vào đây để biết thêm thông tin',
    description: 'Đừng bỏ lỡ nội dung này từ nhà tài trợ của chúng tôi',
    promotedByText: 'Quảng cáo bởi',
    advertiser: 'Nhà tài trợ',
    ctaText: 'Xem thêm',
    days: 'Ngày',
    hours: 'Giờ',
    minutesAbbreviation: 'Phút',
    secondsAbbreviation: 'Giây'
  },
  'zh-cn': {
    title: '点击此处了解更多资讯',
    description: '不要错过我们赞助商的内容',
    promotedByText: '广告来自',
    advertiser: '赞助商',
    ctaText: '查看更多',
    days: '天',
    hours: '小时',
    minutesAbbreviation: '分',
    secondsAbbreviation: '秒'
  },
  'zh-hk': {
    // AI generated
    title: '點擊此處了解更多資訊',
    description: '不要錯過我們贊助商的內容',
    promotedByText: '廣告由',
    advertiser: '贊助商',
    ctaText: '查看更多',
    days: '日',
    hours: '小時',
    minutesAbbreviation: '分',
    secondsAbbreviation: '秒'
  }
};

export const FRENCH_DOMAINS = [
  'lespac.com',
  'rds.ca',
  'noovomoi.ca',
  'sympatico.ca',
  'noovo.ca',
  'lookdujour.ca',
  'lapresse.ca',
  'meilleurstrucs.com',
  'toutacoup.ca',
  '985fm.ca',
  'ledevoir.com',
  'francoischarron.com',
  'lechodelaval.ca',
  'lesaffaires.com',
  'journalmetro.com',
  'zone911.com',
  'journalexpress.ca',
  'neomedia.com',
  'kadogagnant.ca',
  'dessinsdrummond.com',
  'cimtchau.ca',
  'sorel-tracyexpress.ca',
  'droit-inc.com',
  'fm93.com',
  'rythmefm.com',
  'recettedemaman.com',
  'ckoi.com',
  'lanouvelle.net',
  'chamblyexpress.ca',
  'watchmojo.com',
  'radiox.com',
  'fm1047.ca',
  'inmemoriam.ca',
  'lereflet.qc.ca',
  'tvagatineau.ca',
  'journalsaint-francois.ca',
  'stockchase.com',
  'monmatane.com',
  'domainebleu.ca',
  'laction.com',
  'macotenord.com',
  'monjoliette.com',
  'journallesoir.ca',
  'cybersoleil.com',
  'lecitoyenrouynlasarre.com',
  'lecitoyenvaldoramos.com',
  'occasionenor.com',
  'montrealracing.com',
  'cime.fm',
  'wknd.fm',
  'valleedurichelieuexpress.ca',
  'lecourrierdusud.ca',
  '919sports.ca',
  'leclaireurprogres.ca',
  'lechodelarivenord.ca',
  'lerefletdulac.com',
  'mabeauce.com',
  'cjso.ca',
  'monvicto.com',
  '957kyk.com',
  'fm1069.ca',
  'fm1033.ca',
  'fm1077.ca',
  'lavantage.qc.ca',
  'quebechebdo.com',
  'secretaire-inc.com',
  'distances.plus',
  'm1029.com',
  'monthetford.com',
  'journaldelevis.com',
  'canadafrancais.com',
  'montemiscouata.com',
  'thereview.ca',
  'lhebdodustmaurice.com',
  'lhebdojournal.com',
  'journalacces.ca',
  'portail-assurance.ca',
  'lavantagegaspesien.com',
  'larevue.qc.ca',
  'monlatuque.com',
  'courrierfrontenac.qc.ca',
  'lavoixdusud.com',
  'genie-inc.com',
  'lejournaldejoliette.ca',
  'lechodelatuque.com',
  'tvaabitibi.ca',
  'lecourriersud.com',
  'planeteradio.ca',
  'lavibe.fm',
  'hebdorivenord.com',
  'lavantposte.ca',
  'beaucemedia.ca',
  'espresso-jobs.com',
  'journallenord.com',
  'monlimoilou.com',
  'courrierdeportneuf.com',
  'lechodemaskinonge.com',
  'montreal.tv',
  'lelacstjean.com',
  'thesarniajournal.ca',
  'haliburtonecho.ca',
  'guidebateau.com',
  'blvd.fm',
  'granbyexpress.com',
  'lactiondautray.com',
  'lhebdomekinacdeschenaux.ca',
  'monquartier.quebec',
  'lexpressmontcalm.com',
  'lepeuplelotbiniere.ca',
  'nouvelleshebdo.com',
  'monsaintsauveur.com',
  'wowfm.com',
  'monsaintroch.com',
  'journaldechambly.com',
  'letoiledulac.ca',
  'guidevacances.ca',
  'gaspesienouvelles.com',
  'estrieplus.com',
  'o973.ca',
  'monmontcalm.com',
  'lasentinelle.ca',
  'leprogres.net',
  'chaleursnouvelles.com',
  'capitalerock.ca',
  'vosclassees.ca',
  'o1035.ca',
  'versants.com',
  'hitcountry.com',
  'o983.ca',
  'laveniretdesrivieres.com',
  'journalleguide.com',
  'o1015.ca',
  'noovoabitibi.ca',
  'plaisir1019.com',
  'thecosmos.ca',
  'plaisir1067.com',
  'coupdoeil.info',
  'o991.ca',
  'o953.ca',
  'o971.ca',
  'plaisir1055.com',
  'creationsdici.ca',
  'nouvelleliberte.ca',
  'noovogatineau.ca',
  'pop965.ca',
  'lerichelieu.ca',
  'pmedici.ca',
  'restosdici.ca',
  'wow971.ca',
  'bouffedici.ca',
  'montreal.wknd.fm',
  'forumquad.com',
  'plaisir941.com',
  'mamanpourlavie.com',
  'reseaucontact.com',
  'jobboom.com',
  'pagesjaunes.ca',
  'meteomedia.com',
  'journaldemontreal.com',
  'tvanouvelles.ca',
  'journaldequebec.com',
  'tabloid.co',
  'tvasports.ca',
  '7jours.ca',
  'lesacdechips.com',
  'guidetoautoweb.com',
  'salutbonjour.ca',
  'recettes.qc.ca',
  'coupdeponce.com',
  'billie.ca',
  'guideautoweb.com',
  'clindoeil.ca',
  'tvhebdo.ca',
  'pesesurstart.com',
  'coupdepouce.com',
  'espaces.ca',
  'viago.ca',
  'moietcie.ca',
  'addik.tv',
  'ellecanada.com',
  'ici.radio-canada.ca',
  'ici.tou.tv',
  'artv.ca',
  'exploratv.ca',
  'ricardocuisine.com',
  'telequebec.tv',
  'ayoye.com',
  'noovo.info',
  'rcinet.ca',
  'lenecrologue.com',
  'mjardiner.com',
  'rencontresportive.com',
  'autohebdo.net',
  'illicoweb.videotron.com',
  'qub.ca',
  'zeste.ca',
  '24heures.ca',
  'videotron.com',
  'matv.ca',
  'silo57.ca',
  'tvhebdo.com',
  'ecranpartage.ca',
  'oabitibi.ca',
  'monemploi.com',
  'ressourcessante.salutbonjour.ca',
  'letoiledulac.com',
  'lereveil.com',
  'cinoche.com',
  'showbizz.net',
  'jeuxvideo.com',
  'allocine.fr',
  'enfant.com',
  'notretemps.com',
  'familiscope.fr',
  'notrefamille.com',
  'magicmaman.com',
  'marieclaire.fr',
  'cosmopolitan.fr',
  'vanityfair.fr',
  'vogue.fr',
  'gqmagazine.fr',
  'admagazine.fr',
  'voici.fr',
  'gala.fr',
  'businessinsider.fr',
  'caminteresse.fr',
  'capital.fr',
  'cesoirtv.com',
  'cuisineactuelle.fr',
  'femmeactuelle.fr',
  'geo.fr',
  'hbrfrance.fr',
  'programme-tv.net',
  'lecanalauditif.ca',
  'sorstu.ca',
  'baronmag.com',
  'atuvu.ca',
  'labibleurbaine.com',
  'nouvelobs.com',
  'courrierinternational.com',
  'telerama.fr',
  'lemonde.fr',
  'huffingtonpost.fr',
  'suttonquebec.com',
  'royallepage.ca',
  'elle.fr',
  'routard.com',
  'bpmsports.ca',
  'educatout.com',
  'ilesdelamadeleine.com',
  'ellequebec.com',
  'joseedistasio.ca',
  'kpourkatrine.com',
  'veroniquecloutier.com',
  'lebelage.ca',
  'billie.ca',
  'lesacdechips.com',
  'pesesurstart.com',
  'silo57.ca'
];

export const FRENCH_BUNDLES = [
  '715135996',
  'com.rds.multisports',
  '1476103084',
  'com.vmediagroup.noovo',
  '451829003',
  'ca.lapresse.android.lapressemobile',
  '603111926',
  'ca.lapresse.lapresseplus',
  'com.newspaperdirect.jdq.market',
  '512764567',
  '942494435',
  'com.netcosports.andjdm',
  'com.quebecormedia.qubradio',
  '1434066481',
  'com.nurun.lcn',
  '437251500',
  'com.nurun.tva_sports',
  '909307725',
  'tv.accedo.ott.flow.tva.quebecor',
  '1150619411',
  'com.numeriq.tvaplus',
  'com.newspaperdirect.jdm.market',
  '512759149',
  'B07FXQT63T',
  'tv.toutv.androidtv',
  'tv.tou.android',
  '411538137',
  'com.radiocanada.android',
  '364125647'
];
